import { useState } from "react"
import { parseColor } from "react-aria-components/ColorPicker"
import type { Color } from "react-aria-components/ColorPicker"

export const TRANSPARENT = "transparent"

const FALLBACK = "#000000"

// react-aria doesn't parse color names such as "white", which the old picker
// showed correctly. The canvas turns any CSS color into hex or rgba, and keeps
// its previous fill for anything invalid, so that falls back to black.
let canvas: HTMLCanvasElement | undefined

function toCssColor(value: string): string {
  if (typeof document === "undefined") return FALLBACK
  canvas ??= document.createElement("canvas")
  const context = canvas.getContext("2d")
  if (!context) return FALLBACK
  context.fillStyle = FALLBACK
  context.fillStyle = value
  return context.fillStyle
}

/** A value from a consumer: `#RRGGBB`, any CSS color, or "transparent" */
export function toColor(value: string): Color {
  if (value === TRANSPARENT) return parseColor("rgba(0, 0, 0, 0)")
  try {
    return parseColor(value)
  } catch {
    return parseColor(toCssColor(value))
  }
}

/** What a consumer receives: uppercase `#RRGGBB`, or "transparent" */
export function toValue(color: Color): string {
  return color.getChannelValue("alpha") === 0
    ? TRANSPARENT
    : color.toString("hex").toUpperCase()
}

// The area and hue slider keep the opacity they're given, so moving them from
// transparent would stay transparent. Any channel they move means a color was
// picked; picking the transparent tile again moves none.
function isPickedFromTransparent(previous: Color, next: Color) {
  if (previous.getChannelValue("alpha") !== 0) return false
  if (next.getChannelValue("alpha") !== 0) return false
  const before = previous.toFormat(next.getColorSpace())
  return next
    .getColorChannels()
    .some(
      channel =>
        next.getChannelValue(channel) !== before.getChannelValue(channel)
    )
}

/**
 * Holds the color as react-aria's `Color` between renders. Rebuilding it from
 * the hex string each time would lose the hue of greys and black, and snap
 * the hue thumb to red as soon as the user drags into a corner.
 */
export function useColorValue(
  value: string,
  onValueChange: ((value: string) => void) | undefined
) {
  const [color, setColor] = useState(() => toColor(value))
  const [prevValue, setPrevValue] = useState(value)

  if (value !== prevValue) {
    setPrevValue(value)
    if (value !== toValue(color)) setColor(toColor(value))
  }

  const handleChange = (next: Color) => {
    const resolved = isPickedFromTransparent(color, next)
      ? next.withChannelValue("alpha", 1)
      : next
    setColor(resolved)
    onValueChange?.(toValue(resolved))
  }

  return { color, handleChange }
}
